import * as echarts from "echarts";
import React, { useEffect, useRef } from "react";
import { MoMPoint } from "src/helpers";

const MOMChart = ({
  data,
  darkMode,
  isExpand,
  isAnalyzeStock,
}: {
  data: MoMPoint[];
  darkMode: boolean;
  isExpand?: boolean;
  isAnalyzeStock?: boolean;
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.EChartsType | null>(null);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec",
  ];

  const dataLabels = data.map((item) => {
    const month = monthNames[item.month - 1];
    return `${item.year} ${month}`;
  });
  const xAxisYears = data.map((item) => {
    const month = monthNames[item.month - 1];
    return `${item.year} ${month}`;
  });

  const growthValues = data.map((item) => Number(item.avg));
  const total = dataLabels.length;
  const visibleMonths = 12 * 3;
  const startIndex = Math.max(0, total - visibleMonths);
  const endIndex = total - 1;
  const isCompact = Boolean(isAnalyzeStock && !isExpand);

  const option = {
    backgroundColor: "transparent",
    tooltip: {
      trigger: "axis",
      formatter: (params: { dataIndex: number; data: number }[]) => {
        const index = params[0].dataIndex;
        const label = dataLabels[index];
        const value = params[0].data;
        return `${label} : ${value.toFixed(1)}%`;
      },
    },
    grid: {
      top: isCompact ? 24 : 30,
      left: 50,
      right: 10,
      bottom: isCompact ? 36 : 40,
      containLabel: false,
    },
    xAxis: {
      type: "category",
      data: xAxisYears,
      axisLine: { lineStyle: { color: darkMode ? "#ccc" : "#333" } },
      axisTick: { show: false },
      axisLabel: {
        show: true,
        hideOverlap: true,
        color: darkMode ? "#ccc" : "#333",
        fontSize: isCompact ? 10 : 12,
        formatter: (val: string) => val.split(" ")[0],
        interval: (index: number) => {
          if (index === 0) return true;
          const prevYear = xAxisYears[index - 1].split(" ")[0];
          const currYear = xAxisYears[index].split(" ")[0];
          return prevYear !== currYear;
        },
      },
    },
    yAxis: {
      type: "value",
      scale: true,
      splitLine: { show: false },
      axisLine: { lineStyle: { color: darkMode ? "#ccc" : "#333" } },
      axisLabel: { formatter: (val: number) => `${val}%` },
    },

    dataZoom: [
      {
        type: "inside",
        xAxisIndex: 0,
        startValue: startIndex,
        endValue: endIndex,
        zoomLock: isExpand ? false : true,
        moveOnMouseMove: true,
        moveOnMouseWheel: true,
        minSpan: 12,
      },
      {
        type: "slider",
        xAxisIndex: 0,
        height: 4,
        bottom: isCompact ? 6 : 12,
        backgroundColor: darkMode ? "#2e2e2e" : "#e0e0e0",
        fillerColor: darkMode ? "#6b6b6b" : "#333333",
        brushStyle: {
          color: "transparent",
        },
        showDetail: false,
        showDataShadow: false,
        labelFormatter: () => "",
        dataBackground: {
          lineStyle: { color: "transparent" },
          areaStyle: { color: "transparent" },
        },
        handleIcon: "M512 512 m-20 0 a20 20 0 1 0 40 0 a20 20 0 1 0 -40 0",
        handleSize: 10,
        handleColor: "#ffffff",
        borderRadius: 2,
        borderColor: "transparent",
        startValue: startIndex,
        endValue: endIndex,
        minSpan: 12,
      },
    ],
    series: [
      {
        name: "YoY Return",
        type: "line",
        smooth: true,
        data: growthValues,
        lineStyle: { color: "#999", width: 1.5 },
        areaStyle: {
          color: "rgba(150, 150, 150, 0.3)",
        },
        symbol: "circle",
        symbolSize: isCompact ? 4 : 6,
        itemStyle: {
          color: (params: { value: number }) =>
            params.value >= 0 ? "#FFD700" : "#FF4C4C",
        },
        label: {
          show: true,
          position: "top",
          fontSize: isCompact ? 9 : 10,
          formatter: (params: { dataIndex: number; value: number }) => {
            return params.dataIndex % 2 === 0
              ? `${params.value.toFixed(1)}%`
              : "";
          },
        },
        labelLayout: { hideOverlap: true },
      },
    ],
  };

  useEffect(() => {
    if (chartRef.current) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(
          chartRef.current,
          darkMode ? "dark" : "light",
        );
      }
      chartInstance.current.setOption(option);
      chartInstance.current.resize();
    }

    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener("resize", handleResize);
    const resizeObserver =
      typeof ResizeObserver !== "undefined" && chartRef.current
        ? new ResizeObserver(handleResize)
        : null;
    if (chartRef.current && resizeObserver) {
      resizeObserver.observe(chartRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserver?.disconnect();
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, darkMode, isAnalyzeStock, isExpand]);

  return (
    <div
      ref={chartRef}
      id="mom-chart"
      className={`w-full ${isExpand || isAnalyzeStock ? "h-full min-h-0" : "h-[30vh]"}`}
    />
  );
};

export default React.memo(MOMChart);
